import React from "react";
import { Card, Typography, Progress, Button } from "antd";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { useNavigate } from "react-router-dom";
import useUser from "../../Hooks/useUser";
import "./TeamMembersAnalytics.scss";

const { Text } = Typography;

interface TeamMembersAnalyticsProps {
  totalMembers: number;
  recruitersLimit: number;
  activeCount: number;
  inactiveCount: number;
  availableCount: number;
  unavailableCount: number;
  userRole?: string;
}

const CHART_COLORS = {
  success: "#52c41a",
  error: "#ff4d4f",
  warning: "#faad14",
  primary: "#1890ff",
  placeholder: "#d9d9d9",
};

interface DoughnutChartCardProps {
  title: string;
  data: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  total: number;
}

const DoughnutChartCard: React.FC<DoughnutChartCardProps> = ({
  title,
  data,
  total,
}) => {
  const filteredData = data.filter((item) => item.value > 0);

  return (
    <Card className="team-member-stat-card team-member-stat-card--doughnut" size="small">
      <div className="team-member-stat-card__doughnut-content">
        <Text strong className="team-member-stat-card__title">
          {title}
        </Text>
        <div className="team-member-stat-card__doughnut-body">
          <div className="team-member-stat-card__chart-wrapper">
            <ResponsiveContainer width="100%" height={110}>
              <PieChart>
                <Pie
                  data={filteredData.length > 0 ? filteredData : [{ name: "No Data", value: 1, color: CHART_COLORS.placeholder }]}
                  cx="50%"
                  cy="50%"
                  innerRadius={28}
                  outerRadius={42}
                  paddingAngle={2}
                  dataKey="value"
                  labelLine={false}
                  label={(props: any) => {
                    const value = props.value as number;
                    const name = props.name as string;
                    if (value === 0 || name === "No Data") return "";
                    return `${name}: ${value}`;
                  }}
                >
                  {(filteredData.length > 0 ? filteredData : [{ color: CHART_COLORS.placeholder }]).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [value, ""]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="team-member-stat-card__chart-center">
              <Text strong className="team-member-stat-card__chart-total">
                {total}
              </Text>
              <Text type="secondary" className="team-member-stat-card__chart-label">
                Total
              </Text>
            </div>
          </div>
          <div className="team-member-stat-card__legend">
            {filteredData.map((item, index) => (
              <div
                key={`${item.name}-${item.value}-${index}`}
                className="team-member-stat-card__legend-item"
              >
                <span
                  className="team-member-stat-card__legend-color"
                  style={{ backgroundColor: item.color }}
                />
                <Text type="secondary" className="team-member-stat-card__legend-text">
                  {item.name}: {item.value}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

const TeamMembersAnalytics: React.FC<TeamMembersAnalyticsProps> = ({
  totalMembers,
  recruitersLimit,
  activeCount,
  inactiveCount,
  availableCount,
  unavailableCount,
  userRole,
}) => {
  const navigate = useNavigate();
  const { user } = useUser();

  const activeData = [
    { name: "Active", value: activeCount, color: CHART_COLORS.success },
    { name: "Inactive", value: inactiveCount, color: CHART_COLORS.error },
  ];

  const availableData = [
    { name: "Available", value: availableCount, color: CHART_COLORS.success },
    { name: "Unavailable", value: unavailableCount, color: CHART_COLORS.error },
  ];

  return (
    <div className="team-members-analytics">
      <div className="team-members-analytics__container">
        <div className="team-members-analytics__row">
          {/* Total Members Card */}
          <Card className="team-member-stat-card team-member-stat-card--total" size="small">
            <div className="team-member-stat-card__total-content">
              <Text strong className="team-member-stat-card__title">
                Total Members
              </Text>
              <div className="team-member-stat-card__total-value">
                <Text strong className="team-member-stat-card__value">
                  {recruitersLimit === -1 ? (
                    <>
                      {totalMembers} / <span style={{ color: CHART_COLORS.success }}>Unlimited</span>
                    </>
                  ) : (
                    `${totalMembers} / ${recruitersLimit}`
                  )}
                </Text>
              </div>
              {recruitersLimit !== -1 && (
                <Progress 
                  percent={Math.round((totalMembers / recruitersLimit) * 100)} 
                  showInfo={false} 
                  strokeColor={CHART_COLORS.primary}
                  style={{ marginTop: 8 }}
                />
              )}
              <Text 
                type={recruitersLimit !== -1 && totalMembers >= recruitersLimit ? "danger" : "secondary"}
                className="team-member-stat-card__limit-text"
              >
                {recruitersLimit === -1
                  ? "Unlimited team members allowed"
                  : totalMembers >= recruitersLimit
                    ? "Over limit!"
                    : "Within plan limit"}
              </Text>
              {recruitersLimit !== -1 && totalMembers >= recruitersLimit && user?.role === 'admin' && (
                <Button
                  type="link"
                  size="small"
                  block
                  style={{ marginTop: 8, padding: 0 }}
                  onClick={() => navigate('/accounts')}
                >
                  Upgrade
                </Button>
              )}
            </div>
          </Card>

          {/* Active Members Card */}
          <DoughnutChartCard
            title="Active Members"
            data={activeData}
            total={activeCount + inactiveCount}
          />

          {/* Availability Card */}
          <DoughnutChartCard
            title="Availability"
            data={availableData}
            total={availableCount + unavailableCount}
          />
        </div>
      </div>
    </div>
  );
};

export default TeamMembersAnalytics; 